import {
  faBoxOpen,
  faGift,
  faLayerGroup,
  faPlay,
  faPlus,
  faSliders,
  faTag,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { ConfirmModal, ConfirmModalRef, Spinner } from '@shared/components/ui';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Sort, type SortCallbackData, type SortOption } from '@shared/components/ui/Sort';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { Tabs, TabsContent } from '@shared/components/ui/shadcn/tabs';
import { useToast } from '@shared/hooks/use-toast';
import { useAuthStore } from '@shared/stores/authStore';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { usePackagingRulesets, usePackagingSimulator } from '../../hooks';
import {
  BoxGroupModel,
  CreateMarketingRuleConditionPayload,
  LocalMarketingRule,
  MarketingRuleConditionFromApi,
} from '../../models/PackagingRulesets';
import { BoxGroupsSelector } from '../display/BoxGroupsSelector';
import { getGroupPackagingItemIds } from '../display/boxGroups.utils';
import { CreateExceptionModal, CreateExceptionModalRef } from '../modals/CreateExceptionModal';
import {
  CreateMarketingRuleModal,
  CreateMarketingRuleModalRef,
  CreateMarketingRuleSubmitInput,
} from '../modals/CreateMarketingRuleModal';
import {
  EditMarketingRuleModal,
  EditMarketingRuleModalRef,
  EditMarketingRuleSubmitInput,
} from '../modals/EditMarketingRuleModal';
import { EditExceptionModal, EditExceptionModalRef, EditExceptionSubmitPayload } from '../modals/EditExceptionModal';
import { PackagingRulesetsClientBreadcrumb } from '../PackagingRulesetsClientBreadcrumb';

const GROUP_PALETTE: Array<{ colorClass: string; iconClass: string }> = [
  { colorClass: 'bg-bg-success', iconClass: 'text-success' },
  { colorClass: 'bg-bg-info', iconClass: 'text-info' },
  { colorClass: 'bg-badge-bg-status-3', iconClass: 'text-badge-status-3' },
  { colorClass: 'bg-bg-warning', iconClass: 'text-warning' },
  { colorClass: 'bg-badge-bg-status-2', iconClass: 'text-badge-status-2' },
];

const getPalette = (index: number) => GROUP_PALETTE[index % GROUP_PALETTE.length];

/** Migas estándar cuando la vista no va bajo /clients/:id — Standard breadcrumbs when not under client route */
const PackagingRulesetsSelfBreadcrumb = () => {
  const { t } = useTranslation('packagingRulesetsModule');
  usePageNavigation({
    breadcrumbs: [{ label: t('title') }],
  });
  return null;
};

const findGroupForItem = (groups: BoxGroupModel[], packagingItemId: number | null) =>
  groups.find((group) => getGroupPackagingItemIds(group).includes(packagingItemId ?? -1)) ?? null;

// Cuántos grupos se muestran inicialmente en cada selector; "ver más" suma esta cantidad.
const BOX_GROUPS_PAGE_SIZE_DESKTOP = 6;
const BOX_GROUPS_PAGE_SIZE_MOBILE = 3;
const MOBILE_BREAKPOINT_PX = 768;

const getBoxGroupsPageSize = () => {
  if (typeof window === 'undefined') return BOX_GROUPS_PAGE_SIZE_DESKTOP;
  return window.innerWidth < MOBILE_BREAKPOINT_PX ? BOX_GROUPS_PAGE_SIZE_MOBILE : BOX_GROUPS_PAGE_SIZE_DESKTOP;
};

type BoxGroupsSort = 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc';

// Ordena un array de grupos de cajas de forma local sin tocar la API.
const sortBoxGroupsLocally = (groups: BoxGroupModel[], sort: BoxGroupsSort): BoxGroupModel[] => {
  const sorted = [...groups];
  if (sort === 'name_asc') return sorted.sort((a, b) => a.name.localeCompare(b.name));
  if (sort === 'name_desc') return sorted.sort((a, b) => b.name.localeCompare(a.name));
  if (sort === 'id_asc') return sorted.sort((a, b) => a.id - b.id);
  return sorted.sort((a, b) => b.id - a.id);
};

const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    const msg = data.message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
    const errors = data.errors as Record<string, string[] | undefined> | undefined;
    if (errors && typeof errors === 'object') {
      for (const key of Object.keys(errors)) {
        const arr = errors[key];
        if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'string') return arr[0];
      }
    }
  }
  return error?.response?.message ?? error?.message ?? fallback;
};

/** Quita acciones duplicadas por packaging_item_id (p. ej. doble ejecución en dev con Strict Mode); conserva la primera. */
const dedupeMarketingActionsByItemId = (
  actions: { packaging_item_id: number; quantity: number }[],
): { packaging_item_id: number; quantity: number }[] => {
  const seen = new Set<number>();
  const out: { packaging_item_id: number; quantity: number }[] = [];
  for (const a of actions) {
    if (seen.has(a.packaging_item_id)) continue;
    seen.add(a.packaging_item_id);
    out.push({ packaging_item_id: a.packaging_item_id, quantity: a.quantity });
  }
  return out;
};

export type MainProps = {
  /** Team hijo cuando un 3PL abre la vista desde un cliente. */
  scopedTeamId?: number;
};

export const Main: FC<MainProps> = ({ scopedTeamId }) => {
  const isClientScoped = scopedTeamId != null;
  const authTeamId = useAuthStore((state) => state.team?.id);
  const conditionTeamId = scopedTeamId ?? authTeamId;
  const { t } = useTranslation('packagingRulesetsModule');
  const { toast } = useToast();

  // Estados de búsqueda (se aplican localmente, sin llamadas extra a la API).
  const [defaultBoxGroupsSearchInput, setDefaultBoxGroupsSearchInput] = useState('');
  const [fallbackBoxGroupsSearchInput, setFallbackBoxGroupsSearchInput] = useState('');
  const [exceptionBoxGroupsSearchInput, setExceptionBoxGroupsSearchInput] = useState('');

  // Estados de ordenamiento local.
  const [defaultBoxGroupsSort, setDefaultBoxGroupsSort] = useState<BoxGroupsSort>('id_desc');
  const [fallbackBoxGroupsSort, setFallbackBoxGroupsSort] = useState<BoxGroupsSort>('id_desc');
  const [exceptionBoxGroupsSort, setExceptionBoxGroupsSort] = useState<BoxGroupsSort>('id_desc');
  const [hasDefaultSortSelection, setHasDefaultSortSelection] = useState(false);
  const [hasFallbackSortSelection, setHasFallbackSortSelection] = useState(false);
  const [hasExceptionSortSelection, setHasExceptionSortSelection] = useState(false);

  // Cantidad de grupos visibles en cada selector (paginación local).
  const [boxGroupsPageSize, setBoxGroupsPageSize] = useState(getBoxGroupsPageSize);
  const [visibleCountDefault, setVisibleCountDefault] = useState(getBoxGroupsPageSize);
  const [visibleCountFallback, setVisibleCountFallback] = useState(getBoxGroupsPageSize);
  const [visibleCountException, setVisibleCountException] = useState(getBoxGroupsPageSize);

  const {
    boxRulesetId,
    marketingRulesetId,
    marketingRulesets,
    countries,
    allBoxGroups,
    isBoxGroupsLoading,
    activeBoxRuleset,
    boxExceptions,
    createException,
    isCreatingException,
    deleteBoxException,
    marketingCatalogItems,
    createMarketingRule,
    isCreatingMarketingRule,
    updateMarketingRule,
    deleteMarketingRule,
    createMarketingRuleset,
    isCreatingMarketingRuleset,
    isMarketingCatalogLoading,
    isBoxExceptionsLoading,
    saveDefaultBoxGroup,
    saveFallbackBoxGroup,
    isUpdatingBoxRuleset,
    isSyncingBoxRuleset,
  } = usePackagingRulesets(scopedTeamId);
  const simulatorMutation = usePackagingSimulator();

  const [activeTab, setActiveTab] = useState<'boxes' | 'marketing'>('boxes');
  const [defaultBoxGroupId, setDefaultBoxGroupId] = useState<number | null>(null);
  const [fallbackBoxGroupId, setFallbackBoxGroupId] = useState<number | null>(null);
  const [localMarketingRules, setLocalMarketingRules] = useState<LocalMarketingRule[]>([]);
  // Ref para no re-sembrar tras el primer seed desde la API (evita pisar adiciones/eliminaciones locales).
  const marketingRulesSeededRef = useRef(false);

  // Estado del modal de edición de regla de marketing.
  const editMarketingRuleModalRef = useRef<EditMarketingRuleModalRef>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeletingRule, setIsDeletingRule] = useState<number | null>(null);
  const [isTogglingRule, setIsTogglingRule] = useState<number | null>(null);
  const confirmDeleteMarketingRuleRef = useRef<ConfirmModalRef>(null);

  // Helpers para construir etiquetas legibles de condiciones de la API.
  const fieldMap: Record<string, string> = {
    total_amount: t('specialRulesModal.fields.total_amount'),
    order_total: t('specialRulesModal.fields.total_amount'),
    country: t('specialRulesModal.fields.country'),
    fragile: t('specialRulesModal.fields.fragile'),
    campaign: t('specialRulesModal.fields.campaign'),
    product_id: t('specialRulesModal.fields.product_id'),
    pack_id: t('specialRulesModal.fields.pack_id'),
  };
  const opMap: Record<string, string> = {
    '=': t('specialRulesModal.operators.eq'),
    '!=': t('specialRulesModal.operators.neq'),
    '>': t('specialRulesModal.operators.gt'),
    '<': t('specialRulesModal.operators.lt'),
    '>=': t('specialRulesModal.operators.gte'),
    '<=': t('specialRulesModal.operators.lte'),
    contains: t('specialRulesModal.operators.contains'),
  };

  const buildSingleConditionLabel = (condition: MarketingRuleConditionFromApi): string => {
    const fieldLabel = fieldMap[condition.field] ?? condition.field;
    if (condition.value_boolean !== null && condition.value_boolean !== undefined) {
      return `${fieldLabel}: ${t(`specialRulesModal.boolean.${condition.value_boolean ? 'true' : 'false'}`)}`;
    }
    const opLabel = opMap[condition.operator] ?? condition.operator;
    const value =
      condition.value_number !== null && condition.value_number !== undefined
        ? String(condition.value_number)
        : (condition.value_string ?? '');
    return `${fieldLabel} ${opLabel} ${value}`;
  };

  // Construye un resumen legible de todas las condiciones agrupando por group_no (Y/O).
  const buildConditionSummaryFromApi = (conditions: MarketingRuleConditionFromApi[]): string | undefined => {
    if (conditions.length === 0) return undefined;
    const groups = new Map<number, MarketingRuleConditionFromApi[]>();
    conditions.forEach((c) => {
      const g = c.group_no ?? 0;
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(c);
    });
    const groupLabels = Array.from(groups.values()).map((group) =>
      group.map(buildSingleConditionLabel).join(` ${t('specialRulesModal.separatorAnd')} `),
    );
    return groupLabels.join(` ${t('specialRulesModal.separatorOr')} `);
  };

  // Siembra localMarketingRules desde la API la primera vez que lleguen datos.
  useEffect(() => {
    const rules = marketingRulesets[0]?.rules;
    // Espera a que el catálogo esté cargado para resolver nombres en acciones.
    if (!rules || marketingRulesSeededRef.current || isMarketingCatalogLoading) return;
    marketingRulesSeededRef.current = true;
    const seenRuleIds = new Set<number>();
    const uniqueRules = rules.filter((rule) => {
      if (seenRuleIds.has(rule.id)) return false;
      seenRuleIds.add(rule.id);
      return true;
    });
    setLocalMarketingRules(
      uniqueRules.map((rule) => ({
        clientKey: `mk-api-${rule.id}`,
        ruleId: rule.id,
        name: rule.name,
        priority: rule.priority,
        active: rule.active,
        isAlways: rule.conditions.length === 0,
        conditions: rule.conditions.map(
          (condition): CreateMarketingRuleConditionPayload => ({
            field: condition.field,
            operator: condition.operator as CreateMarketingRuleConditionPayload['operator'],
            value_type: (condition.value_type as CreateMarketingRuleConditionPayload['value_type']) ?? 'string',
            value_string: condition.value_string ?? undefined,
            value_number: condition.value_number ?? undefined,
            value_boolean: condition.value_boolean ?? undefined,
            group_no: condition.group_no ?? 0,
          }),
        ),
        conditionSummary: buildConditionSummaryFromApi(rule.conditions),
        actions: dedupeMarketingActionsByItemId(
          rule.actions.map((a) => ({ packaging_item_id: a.packaging_item_id, quantity: a.quantity })),
        ).map((a) => {
          const cat = marketingCatalogItems.find((c) => c.id === a.packaging_item_id);
          return {
            packaging_item_id: a.packaging_item_id,
            quantity: a.quantity,
            name: cat?.name ?? String(a.packaging_item_id),
            colorClass: cat?.colorClass ?? 'bg-bg-info',
            iconClass: cat?.iconClass ?? 'text-info',
            imageUrl: cat?.imageUrl,
          };
        }),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingRulesets, marketingCatalogItems, isMarketingCatalogLoading]);

  // Campos del simulador – claves exactas que usa backend en condiciones.
  const [totalAmount, setTotalAmount] = useState('85');
  const [country, setCountry] = useState('ES');
  const [campaign, setCampaign] = useState<string>('');
  const [fragile, setFragile] = useState(false);
  const [isSelectingFallback, setIsSelectingFallback] = useState(false);
  // Flag unificado: cualquier acción en grupos bloquea ambas secciones al mismo tiempo
  const [isGroupSubmitting, setIsGroupSubmitting] = useState(false);
  const isGroupInteractionDisabled = isGroupSubmitting || isUpdatingBoxRuleset || isSyncingBoxRuleset;

  // Empty: dashed + "Añadir" — sin Eliminar. Cualquier otro estado (grid con o sin elección) sí muestra Eliminar.
  const isFallbackEmptyState = !fallbackBoxGroupId && !isSelectingFallback;
  const showFallbackRemoveButton = !isFallbackEmptyState;

  const exceptionModalRef = useRef<CreateExceptionModalRef>(null);
  const marketingRuleModalRef = useRef<CreateMarketingRuleModalRef>(null);
  const confirmRemoveFallbackRef = useRef<ConfirmModalRef>(null);

  // Estado del modal de edición de regla especial (box-exception).
  const editExceptionModalRef = useRef<EditExceptionModalRef>(null);
  const confirmDeleteExceptionRef = useRef<ConfirmModalRef>(null);
  const [isSavingException, setIsSavingException] = useState(false);
  const [isDeletingException, setIsDeletingException] = useState<number | null>(null);
  const [isTogglingException, setIsTogglingException] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => setBoxGroupsPageSize(getBoxGroupsPageSize());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Ajusta el tamaño inicial visible según viewport (móvil: 3, desktop: 6).
    setVisibleCountDefault(boxGroupsPageSize);
    setVisibleCountFallback(boxGroupsPageSize);
    setVisibleCountException(boxGroupsPageSize);
  }, [boxGroupsPageSize]);

  useEffect(() => {
    if (!activeBoxRuleset) return;

    // El backend define selección por grupo (default_box_group_id / fallback_box_group_id).
    // Fallback temporal: si no viene group_id, inferimos por packaging_item_id para compatibilidad.
    // Si default_box_group_id es null explícito, no inferir (sin cajas por defecto).
    let inferredDefaultGroupId: number | null;
    if (activeBoxRuleset.default_box_group_id === null) {
      inferredDefaultGroupId = null;
    } else if (activeBoxRuleset.default_box_group_id != null) {
      inferredDefaultGroupId = activeBoxRuleset.default_box_group_id;
    } else {
      inferredDefaultGroupId =
        findGroupForItem(
          allBoxGroups,
          activeBoxRuleset.default_packaging_item_id ?? activeBoxRuleset.default_packaging_item?.id ?? null,
        )?.id ?? null;
    }
    const inferredFallbackGroupId =
      activeBoxRuleset.fallback_box_group_id ??
      findGroupForItem(
        allBoxGroups,
        activeBoxRuleset.fallback_packaging_item_id ?? activeBoxRuleset.fallback_packaging_item?.id ?? null,
      )?.id ??
      null;

    setDefaultBoxGroupId(inferredDefaultGroupId);
    setFallbackBoxGroupId(inferredFallbackGroupId);
    setIsSelectingFallback(false);
  }, [activeBoxRuleset, allBoxGroups]);

  const selectedDefaultGroup = useMemo(
    () => allBoxGroups.find((group) => group.id === defaultBoxGroupId) ?? null,
    [allBoxGroups, defaultBoxGroupId],
  );

  const selectedFallbackGroup = useMemo(
    () => allBoxGroups.find((group) => group.id === fallbackBoxGroupId) ?? null,
    [allBoxGroups, fallbackBoxGroupId],
  );

  // --- Filtrado, ordenamiento y paginación local para el selector Default ---
  const filteredDefaultBoxGroups = useMemo(() => {
    const search = defaultBoxGroupsSearchInput.trim().toLowerCase();
    let result = search ? allBoxGroups.filter((g) => g.name.toLowerCase().includes(search)) : [...allBoxGroups];
    result = sortBoxGroupsLocally(result, defaultBoxGroupsSort);
    // Mueve el seleccionado al frente cuando no hay búsqueda ni ordenamiento manual activo.
    if (defaultBoxGroupId && !hasDefaultSortSelection && !search) {
      const pinned = result.find((g) => g.id === defaultBoxGroupId);
      if (pinned) result = [pinned, ...result.filter((g) => g.id !== defaultBoxGroupId)];
    }
    return result;
  }, [allBoxGroups, defaultBoxGroupsSearchInput, defaultBoxGroupsSort, defaultBoxGroupId, hasDefaultSortSelection]);

  const orderedDefaultBoxGroups = useMemo(
    () => filteredDefaultBoxGroups.slice(0, visibleCountDefault),
    [filteredDefaultBoxGroups, visibleCountDefault],
  );
  const hasMoreDefaultBoxGroups = filteredDefaultBoxGroups.length > visibleCountDefault;

  // --- Filtrado, ordenamiento y paginación local para el selector Fallback ---
  const filteredFallbackBoxGroups = useMemo(() => {
    const search = fallbackBoxGroupsSearchInput.trim().toLowerCase();
    let result = search ? allBoxGroups.filter((g) => g.name.toLowerCase().includes(search)) : [...allBoxGroups];
    result = sortBoxGroupsLocally(result, fallbackBoxGroupsSort);
    if (fallbackBoxGroupId && !hasFallbackSortSelection && !search) {
      const pinned = result.find((g) => g.id === fallbackBoxGroupId);
      if (pinned) result = [pinned, ...result.filter((g) => g.id !== fallbackBoxGroupId)];
    }
    return result;
  }, [allBoxGroups, fallbackBoxGroupsSearchInput, fallbackBoxGroupsSort, fallbackBoxGroupId, hasFallbackSortSelection]);

  const orderedFallbackBoxGroups = useMemo(
    () => filteredFallbackBoxGroups.slice(0, visibleCountFallback),
    [filteredFallbackBoxGroups, visibleCountFallback],
  );
  const hasMoreFallbackBoxGroups = filteredFallbackBoxGroups.length > visibleCountFallback;

  // --- Filtrado, ordenamiento y paginación local para el modal de Excepciones ---
  const filteredExceptionBoxGroups = useMemo(() => {
    const search = exceptionBoxGroupsSearchInput.trim().toLowerCase();
    const base = search ? allBoxGroups.filter((g) => g.name.toLowerCase().includes(search)) : [...allBoxGroups];
    return sortBoxGroupsLocally(base, exceptionBoxGroupsSort);
  }, [allBoxGroups, exceptionBoxGroupsSearchInput, exceptionBoxGroupsSort]);

  const visibleExceptionBoxGroups = useMemo(
    () => filteredExceptionBoxGroups.slice(0, visibleCountException),
    [filteredExceptionBoxGroups, visibleCountException],
  );
  const hasMoreExceptionBoxGroups = filteredExceptionBoxGroups.length > visibleCountException;

  const handleSelectDefaultGroup = async (group: BoxGroupModel) => {
    setIsGroupSubmitting(true);
    try {
      await saveDefaultBoxGroup(group.id);
      setDefaultBoxGroupId(group.id);
      toast({
        title: t('toasts.defaultGroupSavedTitle'),
        description: t('toasts.defaultGroupSavedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('toasts.errorTitle'),
        description: error?.message ?? t('toasts.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsGroupSubmitting(false);
    }
  };

  const handleSelectNoDefaultBoxes = async () => {
    setIsGroupSubmitting(true);
    try {
      await saveDefaultBoxGroup(null);
      setDefaultBoxGroupId(null);
      toast({
        title: t('toasts.defaultGroupSavedTitle'),
        description: t('toasts.defaultGroupSavedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('toasts.errorTitle'),
        description: error?.message ?? t('toasts.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsGroupSubmitting(false);
    }
  };

  const isNoDefaultBoxesSelected = activeBoxRuleset != null && defaultBoxGroupId === null;

  const handleSelectFallbackGroup = async (group: BoxGroupModel) => {
    setIsGroupSubmitting(true);
    try {
      await saveFallbackBoxGroup(group.id);
      setFallbackBoxGroupId(group.id);
      setIsSelectingFallback(false);
      toast({
        title: t('toasts.fallbackGroupSavedTitle'),
        description: t('toasts.fallbackGroupSavedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('toasts.errorTitle'),
        description: error?.message ?? t('toasts.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsGroupSubmitting(false);
    }
  };

  const nextMarketingPriority = useMemo(() => {
    if (localMarketingRules.length === 0) return 100;
    return Math.max(...localMarketingRules.map((r) => r.priority), 0) + 5;
  }, [localMarketingRules]);

  const packagingTabs = useMemo(
    () => [
      {
        key: 'boxes' as const,
        label: t('tabs.boxes'),
        icon: faBoxOpen,
      },
      {
        key: 'marketing' as const,
        label: t('tabs.marketing'),
        icon: faGift,
      },
    ],
    [t],
  );

  const boxGroupsSortOptions = useMemo(
    (): SortOption[] => [
      { field: 'id', direction: 'desc', label: 'Mas nuevo a mas viejo' },
      { field: 'id', direction: 'asc', label: 'Mas viejo a mas nuevo' },
      { field: 'name', direction: 'asc', label: 'Alfabetico A-Z' },
      { field: 'name', direction: 'desc', label: 'Alfabetico Z-A' },
    ],
    [],
  );

  const toSortValue = (field: string, direction: 'asc' | 'desc'): 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc' => {
    if (field === 'id' && direction === 'asc') return 'id_asc';
    if (field === 'name' && direction === 'asc') return 'name_asc';
    if (field === 'name' && direction === 'desc') return 'name_desc';
    return 'id_desc';
  };

  const parseSortValue = (sort: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => {
    if (sort === 'id_asc') return { field: 'id', direction: 'asc' as const };
    if (sort === 'name_asc') return { field: 'name', direction: 'asc' as const };
    if (sort === 'name_desc') return { field: 'name', direction: 'desc' as const };
    return { field: 'id', direction: 'desc' as const };
  };

  const simulatorData = simulatorMutation.data;
  const evaluatedBoxId = simulatorData?.box?.packaging_item_id ?? null;
  const evaluatedItems = simulatorData?.marketing?.items;

  /** Nombre del item de caja resultante, buscado en el catálogo de grupos. */
  const evaluatedBoxName = useMemo(() => {
    if (!evaluatedBoxId) return null;
    for (const group of allBoxGroups) {
      if (getGroupPackagingItemIds(group).includes(evaluatedBoxId)) return group.name;
    }
    return `#${evaluatedBoxId}`;
  }, [allBoxGroups, evaluatedBoxId]);

  /** Items de marketing resueltos contra el catálogo. */
  const resolvedMarketingItems = useMemo(() => {
    return (evaluatedItems ?? []).map((item) => {
      const catalogItem = marketingCatalogItems.find((c) => c.id === item.packaging_item_id);
      return {
        ...item,
        name: catalogItem?.name ?? `#${item.packaging_item_id}`,
        colorClass: 'bg-bg-info',
        iconClass: 'text-info',
      };
    });
  }, [evaluatedItems, marketingCatalogItems]);

  // Resumen de la sección de marketing.
  const activeMarketingRulesCount = useMemo(
    () => localMarketingRules.filter((rule) => rule.active).length,
    [localMarketingRules],
  );
  // Materiales de marketing únicos (por packaging_item_id) en acciones de todas las reglas (activas e inactivas).
  const differentMarketingItemsCount = useMemo(() => {
    const uniqueIds = new Set<number>();
    localMarketingRules.forEach((rule) => rule.actions.forEach((action) => uniqueIds.add(action.packaging_item_id)));
    return uniqueIds.size;
  }, [localMarketingRules]);

  const countryOptions = [
    { value: 'ES', label: t('simulator.countries.es') },
    { value: 'CL', label: t('simulator.countries.cl') },
    { value: 'MX', label: t('simulator.countries.mx') },
    { value: 'CO', label: t('simulator.countries.co') },
  ];

  // Catálogo estático de campañas hasta que exista endpoint.
  const campaignOptions = [
    { value: '', label: t('simulator.campaignNone') },
    { value: 'black_friday', label: 'Black Friday' },
    { value: 'summer', label: t('simulator.campaigns.summer') },
    { value: 'christmas', label: t('simulator.campaigns.christmas') },
    { value: 'back_to_school', label: t('simulator.campaigns.backToSchool') },
  ];

  const handleSimulate = async () => {
    // Permite simular aunque falte uno de los dos rulesets (resultado parcial).
    if (!boxRulesetId && !marketingRulesetId) {
      toast({
        title: t('toasts.missingRulesetTitle'),
        description: t('toasts.missingRulesetDescription'),
        variant: 'destructive',
      });
      return;
    }

    const result = await simulatorMutation.mutateAsync({
      boxRulesetId,
      marketingRulesetId,
      context: {
        total_amount: Number(totalAmount || 0),
        country,
        fragile,
        campaign: campaign || null,
      },
    });

    // Mostrar advertencia si alguna evaluación parcial falló.
    if (result.box.error && result.box.error !== 'no_box_ruleset') {
      toast({
        title: t('simulator.boxEvalError'),
        description: result.box.error,
        variant: 'destructive',
      });
    }
    if (result.marketing.error && result.marketing.error !== 'no_marketing_ruleset') {
      toast({
        title: t('simulator.marketingEvalError'),
        description: result.marketing.error,
        variant: 'destructive',
      });
    }
  };

  const handleCreateException = async ({
    exception,
    conditions,
  }: {
    exception: Parameters<typeof createException>[0]['exception'];
    conditions: Parameters<typeof createException>[0]['conditions'];
  }) => {
    if (!boxRulesetId) {
      toast({
        title: t('toasts.missingRulesetTitle'),
        description: t('toasts.missingRulesetDescription'),
        variant: 'destructive',
      });
      return;
    }
    try {
      await createException({ rulesetId: boxRulesetId, exception, conditions });
      toast({
        title: t('toasts.exceptionCreatedTitle'),
        description: t('toasts.exceptionCreatedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('toasts.errorTitle'),
        description: getApiErrorMessage(error, t('toasts.errorDescription')),
        variant: 'destructive',
      });
    }
  };

  const handleCreateMarketingRule = async (input: CreateMarketingRuleSubmitInput) => {
    try {
      let rulesetId = marketingRulesetId;
      if (!rulesetId) {
        const ruleset = await createMarketingRuleset();
        rulesetId = ruleset.id;
      }

      const actionsForApi = dedupeMarketingActionsByItemId(input.actions);

      const created = await createMarketingRule({
        rulesetId,
        name: input.name,
        priority: input.priority,
        active: input.active,
        isAlways: input.isAlways,
        conditions: input.conditions,
        actions: actionsForApi,
      });

      const nextRule: LocalMarketingRule = {
        clientKey: `mk-${Date.now()}-${Math.random()}`,
        ruleId: created.id,
        name: input.name,
        priority: input.priority,
        active: input.active,
        isAlways: input.isAlways,
        conditions: input.isAlways ? [] : (input.conditions ?? []),
        conditionSummary: input.isAlways ? undefined : input.conditionSummary,
        actions: actionsForApi.map((a) => {
          const cat = marketingCatalogItems.find((c) => c.id === a.packaging_item_id);
          return {
            packaging_item_id: a.packaging_item_id,
            quantity: a.quantity,
            name: cat?.name ?? String(a.packaging_item_id),
            colorClass: cat?.colorClass ?? 'bg-bg-info',
            iconClass: cat?.iconClass ?? 'text-info',
            imageUrl: cat?.imageUrl,
          };
        }),
      };

      setLocalMarketingRules((prev) => (prev.some((r) => r.ruleId === nextRule.ruleId) ? prev : [...prev, nextRule]));
      toast({
        title: t('marketing.toasts.createdTitle'),
        description: t('marketing.toasts.createdDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('toasts.errorTitle'),
        description: getApiErrorMessage(error, t('toasts.errorDescription')),
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Abre el modal de edición de regla especial con los datos actuales.
  const handleOpenEditException = (exception: (typeof boxExceptions)[number]) => {
    editExceptionModalRef.current?.open(exception);
  };

  // Guarda edición completa (grupo + condiciones + meta) recreando excepción y eliminando la original.
  const handleSaveEditException = async (input: EditExceptionSubmitPayload) => {
    if (!boxRulesetId) return;
    setIsSavingException(true);
    try {
      await createException({
        rulesetId: boxRulesetId,
        exception: input.exception,
        conditions: input.conditions,
      });
      await deleteBoxException(input.originalExceptionId);
      toast({
        title: t('boxes.specialRules.toasts.updatedTitle'),
        description: t('boxes.specialRules.toasts.updatedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('boxes.specialRules.toasts.updateErrorTitle'),
        description: getApiErrorMessage(error, t('boxes.specialRules.toasts.updateErrorDescription')),
        variant: 'destructive',
      });
    } finally {
      setIsSavingException(false);
    }
  };

  // Solicita confirmación y elimina la regla especial vía DELETE.
  const handleDeleteException = (exception: (typeof boxExceptions)[number]) => {
    confirmDeleteExceptionRef.current?.open(async () => {
      setIsDeletingException(exception.id);
      try {
        await deleteBoxException(exception.id);
        toast({
          title: t('boxes.specialRules.toasts.deletedTitle'),
          description: t('boxes.specialRules.toasts.deletedDescription'),
        });
      } catch {
        toast({
          title: t('boxes.specialRules.toasts.deleteErrorTitle'),
          description: t('boxes.specialRules.toasts.deleteErrorDescription'),
          variant: 'destructive',
        });
      } finally {
        setIsDeletingException(null);
      }
    });
  };

  // Cambia estado activa/inactiva de regla especial desde la lista.
  const handleToggleExceptionActive = async (exception: (typeof boxExceptions)[number], active: boolean) => {
    setIsTogglingException(exception.id);
    try {
      if (!boxRulesetId || !exception.box_group_id) {
        throw new Error(t('boxes.specialRules.toasts.updateErrorDescription'));
      }

      // Usamos la misma estrategia robusta de edición completa:
      // crear nueva excepción con estado actualizado y luego eliminar la anterior.
      await createException({
        rulesetId: boxRulesetId,
        exception: {
          name: exception.name,
          box_group_id: exception.box_group_id,
          priority: exception.priority,
          active,
        },
        conditions: (exception.conditions ?? []).map((condition) => ({
          field: condition.field,
          operator: condition.operator,
          value_type: condition.value_type,
          value_string: condition.value_string ?? undefined,
          value_number: condition.value_number ?? undefined,
          value_boolean: condition.value_boolean ?? undefined,
          value_json: condition.value_json,
          group_no: condition.group_no ?? 0,
        })),
      });
      await deleteBoxException(exception.id);

      toast({
        title: t('boxes.specialRules.toasts.updatedTitle'),
        description: t('boxes.specialRules.toasts.updatedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('boxes.specialRules.toasts.updateErrorTitle'),
        description: getApiErrorMessage(error, t('boxes.specialRules.toasts.updateErrorDescription')),
        variant: 'destructive',
      });
    } finally {
      setIsTogglingException(null);
    }
  };

  // Abre el modal de edición con los datos completos actuales de la regla.
  const handleOpenEditMarketingRule = (rule: LocalMarketingRule) => {
    editMarketingRuleModalRef.current?.open(rule);
  };

  // Guarda edición completa (meta + condiciones + materiales) recreando la regla y reemplazando la anterior.
  const handleSaveEditMarketingRule = async (input: EditMarketingRuleSubmitInput) => {
    setIsSavingEdit(true);
    try {
      if (!marketingRulesetId) {
        throw new Error(t('toasts.missingRulesetDescription'));
      }

      const actionsForApi = dedupeMarketingActionsByItemId(input.actions);

      // 1) Crear nueva versión con todo el contenido editado.
      const created = await createMarketingRule({
        rulesetId: marketingRulesetId,
        name: input.name,
        priority: input.priority,
        active: input.active,
        isAlways: input.isAlways,
        conditions: input.conditions,
        actions: actionsForApi,
      });

      // 2) Eliminar versión anterior (mismo contrato que antes: POST nueva regla + DELETE id viejo).
      await deleteMarketingRule(input.ruleId);

      const nextRule: LocalMarketingRule = {
        clientKey: `mk-edit-${Date.now()}-${Math.random()}`,
        ruleId: created.id,
        name: input.name,
        priority: input.priority,
        active: input.active,
        isAlways: input.isAlways,
        conditions: input.isAlways ? [] : (input.conditions ?? []),
        conditionSummary: input.isAlways ? undefined : input.conditionSummary,
        actions: actionsForApi.map((a) => {
          const cat = marketingCatalogItems.find((c) => c.id === a.packaging_item_id);
          return {
            packaging_item_id: a.packaging_item_id,
            quantity: a.quantity,
            name: cat?.name ?? String(a.packaging_item_id),
            colorClass: cat?.colorClass ?? 'bg-bg-info',
            iconClass: cat?.iconClass ?? 'text-info',
            imageUrl: cat?.imageUrl,
          };
        }),
      };

      setLocalMarketingRules((prev) => prev.map((r) => (r.ruleId === input.ruleId ? nextRule : r)));
      toast({ title: t('marketing.toasts.updatedTitle'), description: t('marketing.toasts.updatedDescription') });
    } catch (error: any) {
      toast({
        title: t('marketing.toasts.updateErrorTitle'),
        description: getApiErrorMessage(error, t('marketing.toasts.updateErrorDescription')),
        variant: 'destructive',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Elimina una regla vía DELETE y la quita del estado local.
  const handleDeleteMarketingRule = (rule: LocalMarketingRule) => {
    confirmDeleteMarketingRuleRef.current?.open(async () => {
      setIsDeletingRule(rule.ruleId);
      try {
        await deleteMarketingRule(rule.ruleId);
        setLocalMarketingRules((prev) => prev.filter((r) => r.ruleId !== rule.ruleId));
        toast({ title: t('marketing.toasts.deletedTitle'), description: t('marketing.toasts.deletedDescription') });
      } catch (error: any) {
        toast({
          title: t('marketing.toasts.deleteErrorTitle'),
          description: getApiErrorMessage(error, t('marketing.toasts.deleteErrorDescription')),
          variant: 'destructive',
        });
      } finally {
        setIsDeletingRule(null);
      }
    });
  };

  // Cambia el estado activo de una regla vía PUT y actualiza el estado local.
  const handleToggleMarketingRuleActive = async (rule: LocalMarketingRule, active: boolean) => {
    setIsTogglingRule(rule.ruleId);
    try {
      await updateMarketingRule({ ruleId: rule.ruleId, name: rule.name, priority: rule.priority, active });
      setLocalMarketingRules((prev) => prev.map((r) => (r.ruleId === rule.ruleId ? { ...r, active } : r)));
      toast({
        title: t('marketing.toasts.updatedTitle'),
        description: t('marketing.toasts.updatedDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('marketing.toasts.updateErrorTitle'),
        description: getApiErrorMessage(error, t('marketing.toasts.updateErrorDescription')),
        variant: 'destructive',
      });
    } finally {
      setIsTogglingRule(null);
    }
  };

  return (
    <>
      {!isClientScoped ? <PackagingRulesetsSelfBreadcrumb /> : null}
      {/* Migas desde cliente (3PL) — Client breadcrumbs from client (3PL) */}
      {isClientScoped ? <PackagingRulesetsClientBreadcrumb /> : null}
      <div className="px-6 pt-4 pb-8 xl:pr-[380px]">
        {/* Modal de confirmación para eliminar regla especial */}
        <ConfirmModal ref={confirmDeleteExceptionRef} />
        {/* Modal de confirmación para eliminar regla de marketing */}
        <ConfirmModal ref={confirmDeleteMarketingRuleRef} />
        <CreateExceptionModal
          ref={exceptionModalRef}
          teamId={conditionTeamId}
          boxGroups={visibleExceptionBoxGroups}
          countries={countries}
          boxGroupsSearchValue={exceptionBoxGroupsSearchInput}
          onBoxGroupsSearchChange={setExceptionBoxGroupsSearchInput}
          boxGroupsSortValue={exceptionBoxGroupsSort}
          onBoxGroupsSortChange={(value) => {
            setHasExceptionSortSelection(true);
            setExceptionBoxGroupsSort(value);
          }}
          hasBoxGroupsSortSelection={hasExceptionSortSelection}
          hasMoreBoxGroups={hasMoreExceptionBoxGroups}
          isFetchingMoreBoxGroups={false}
          isBoxGroupsLoading={isBoxGroupsLoading}
          onLoadMoreBoxGroups={() => setVisibleCountException((prev) => prev + boxGroupsPageSize)}
          isSubmitting={isCreatingException}
          onSubmit={handleCreateException}
        />
        <EditExceptionModal
          ref={editExceptionModalRef}
          teamId={conditionTeamId}
          boxGroups={visibleExceptionBoxGroups}
          countries={countries}
          boxGroupsSearchValue={exceptionBoxGroupsSearchInput}
          onBoxGroupsSearchChange={setExceptionBoxGroupsSearchInput}
          boxGroupsSortValue={exceptionBoxGroupsSort}
          onBoxGroupsSortChange={(value) => {
            setHasExceptionSortSelection(true);
            setExceptionBoxGroupsSort(value);
          }}
          hasBoxGroupsSortSelection={hasExceptionSortSelection}
          hasMoreBoxGroups={hasMoreExceptionBoxGroups}
          isFetchingMoreBoxGroups={false}
          isBoxGroupsLoading={isBoxGroupsLoading}
          onLoadMoreBoxGroups={() => setVisibleCountException((prev) => prev + boxGroupsPageSize)}
          isSubmitting={isSavingException}
          onSubmit={handleSaveEditException}
        />
        <CreateMarketingRuleModal
          ref={marketingRuleModalRef}
          catalog={marketingCatalogItems}
          countries={countries}
          defaultPriority={nextMarketingPriority}
          isSubmitting={isCreatingMarketingRule || isCreatingMarketingRuleset}
          onSubmit={handleCreateMarketingRule}
        />
        <EditMarketingRuleModal
          ref={editMarketingRuleModalRef}
          catalog={marketingCatalogItems}
          countries={countries}
          isSubmitting={isSavingEdit}
          onSubmit={handleSaveEditMarketingRule}
        />
        {/* Modal de confirmación para eliminar el grupo de respaldo */}
        <ConfirmModal ref={confirmRemoveFallbackRef} />

        <div className="grid grid-cols-1 gap-6">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'boxes' | 'marketing')}>
            {/* Tabs estilo CRM - Cajas y Materiales */}
            <div
              className="flex bg-card-bg rounded-lg text-sm p-1 mb-2 overflow-x-auto scrollbar-hide light:border-card-border light:border"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="packaging-rulesets-tabs"
            >
              {packagingTabs.map((tab) => {
                const isActive = activeTab === tab.key;

                return (
                  <a
                    key={tab.key}
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab(tab.key);
                    }}
                    className={cn(
                      'px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5',
                      isActive && 'bg-hover-press',
                    )}
                    data-testid={`packaging-${tab.key}-tab`}
                  >
                    <FontAwesomeIcon
                      icon={tab.icon}
                      className={cn('h-3.5 w-3.5', !isActive && 'text-complementary-text')}
                    />
                    <span className={cn(!isActive && 'text-complementary-text')}>{tab.label}</span>
                  </a>
                );
              })}
            </div>

            <TabsContent value="boxes" className="mt-4 space-y-4">
              {/* Grupo por defecto (box-groups) */}
              <SectionBox data-testid="packaging-default-group">
                <div>
                  <h3 className="text-base font-semibold text-text">{t('boxes.defaultGroup.title')}</h3>
                  <p className="text-sm text-subtext mb-4">{t('boxes.defaultGroup.description')}</p>
                </div>
                {defaultBoxGroupId && !selectedDefaultGroup && (
                  <p className="text-xs text-warning mb-4">
                    {t('boxes.defaultGroup.unavailableConfigured', { id: defaultBoxGroupId })}
                  </p>
                )}
                <BoxGroupsSelector
                  groups={orderedDefaultBoxGroups}
                  selectedGroupId={selectedDefaultGroup?.id ?? null}
                  onSelectGroup={handleSelectDefaultGroup}
                  noneOption={{
                    label: t('boxes.defaultGroup.noneOption'),
                    description: t('boxes.defaultGroup.noneOptionDescription'),
                    selected: isNoDefaultBoxesSelected,
                    onSelect: handleSelectNoDefaultBoxes,
                    testId: 'default-group-none',
                  }}
                  disabled={isGroupInteractionDisabled}
                  isLoading={isBoxGroupsLoading}
                  emptyTitle={allBoxGroups.length === 0 ? t('boxes.defaultGroup.emptyTitle') : undefined}
                  emptyText={allBoxGroups.length === 0 ? '' : t('boxes.defaultGroup.searchNoResults')}
                  emptyTextSecond={allBoxGroups.length === 0 ? t('boxes.defaultGroup.emptyContinuation') : undefined}
                  searchValue={defaultBoxGroupsSearchInput}
                  onSearchChange={allBoxGroups.length === 0 ? undefined : setDefaultBoxGroupsSearchInput}
                  searchPlaceholder={t('boxes.defaultGroup.searchPlaceholder')}
                  searchTestId="default-box-groups-search-input"
                  sortSlot={
                    allBoxGroups.length === 0 ? undefined : (
                      <div className="shrink-0">
                        <Sort
                          options={boxGroupsSortOptions}
                          onSort={(data: SortCallbackData) => {
                            setHasDefaultSortSelection(true);
                            setDefaultBoxGroupsSort(toSortValue(data.field, data.direction));
                          }}
                          initialValue={hasDefaultSortSelection ? parseSortValue(defaultBoxGroupsSort) : undefined}
                          defaultLabel="Ordenar"
                          dataTestId="default-box-groups-sort"
                          size="default"
                          className="h-10 w-full justify-between"
                        />
                      </div>
                    )
                  }
                  testIdPrefix="default-group-card"
                  loadingTestId="default-group-loading"
                  emptyTestId="default-group-empty"
                  hasMore={hasMoreDefaultBoxGroups}
                  isFetchingMore={false}
                  onLoadMore={() => setVisibleCountDefault((prev) => prev + boxGroupsPageSize)}
                  loadMoreTestId="default-group-load-more"
                />
              </SectionBox>

              {/* Grupo fallback (box-groups) */}
              <SectionBox data-testid="packaging-fallback-group">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 pr-2">
                    <h3 className="text-base font-semibold text-text">{t('boxes.fallbackGroup.title')}</h3>
                    <p className="text-sm text-subtext mb-4">{t('boxes.fallbackGroup.description')}</p>
                  </div>
                  {showFallbackRemoveButton && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={faTrash}
                      iconPosition="start"
                      onClick={() =>
                        confirmRemoveFallbackRef.current?.open(
                          async () => {
                            setIsGroupSubmitting(true);
                            try {
                              await saveFallbackBoxGroup(null);
                              setFallbackBoxGroupId(null);
                              setIsSelectingFallback(false);
                              toast({
                                title: t('toasts.fallbackGroupSavedTitle'),
                                description: t('toasts.fallbackGroupClearedDescription'),
                              });
                            } catch (error: any) {
                              toast({
                                title: t('toasts.errorTitle'),
                                description: error?.message ?? t('toasts.errorDescription'),
                                variant: 'destructive',
                              });
                            } finally {
                              setIsGroupSubmitting(false);
                            }
                          },
                          undefined,
                          t('boxes.fallbackGroup.removeConfirmTitle'),
                          t('boxes.fallbackGroup.removeConfirmMessage'),
                        )
                      }
                      data-testid="fallback-remove-button"
                      className="shrink-0 text-text hover:text-danger"
                      disabled={isGroupInteractionDisabled}
                    >
                      {t('boxes.fallbackGroup.remove')}
                    </Button>
                  )}
                </div>
                {fallbackBoxGroupId && !selectedFallbackGroup && (
                  <p className="text-xs text-warning mb-2">
                    {t('boxes.fallbackGroup.unavailableConfigured', { id: fallbackBoxGroupId })}
                  </p>
                )}

                {isFallbackEmptyState ? (
                  isBoxGroupsLoading ? (
                    <div className="mt-4 flex justify-center py-8" data-testid="fallback-group-loading">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <div className="mt-4 rounded-xl border border-dashed border-card-border bg-card-bg px-6 py-8 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg-info">
                        <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-info" />
                      </div>
                      <p className="text-base font-medium text-text">{t('boxes.fallbackGroup.emptyTitle')}</p>
                      <p className="mx-auto mt-1 max-w-[420px] text-sm text-subtext">
                        {t('boxes.fallbackGroup.emptyContinuation')}
                      </p>
                      <Button
                        className="mt-4 mx-auto"
                        variant="outline"
                        onClick={() => setIsSelectingFallback(true)}
                        data-testid="fallback-add-button"
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                        {t('boxes.fallbackGroup.add')}
                      </Button>
                    </div>
                  )
                ) : (
                  <BoxGroupsSelector
                    groups={orderedFallbackBoxGroups}
                    selectedGroupId={selectedFallbackGroup?.id ?? null}
                    onSelectGroup={handleSelectFallbackGroup}
                    disabled={isGroupInteractionDisabled}
                    isLoading={isBoxGroupsLoading}
                    emptyTitle={allBoxGroups.length === 0 ? t('boxes.fallbackGroup.selectorEmptyTitle') : undefined}
                    emptyText={allBoxGroups.length === 0 ? '' : t('boxes.defaultGroup.searchNoResults')}
                    emptyTextSecond={
                      allBoxGroups.length === 0 ? t('boxes.fallbackGroup.selectorEmptyContinuation') : undefined
                    }
                    searchValue={fallbackBoxGroupsSearchInput}
                    onSearchChange={allBoxGroups.length === 0 ? undefined : setFallbackBoxGroupsSearchInput}
                    searchPlaceholder={t('boxes.defaultGroup.searchPlaceholder')}
                    searchTestId="fallback-box-groups-search-input"
                    sortSlot={
                      allBoxGroups.length === 0 ? undefined : (
                        <div className="shrink-0">
                          <Sort
                            options={boxGroupsSortOptions}
                            onSort={(data: SortCallbackData) => {
                              setHasFallbackSortSelection(true);
                              setFallbackBoxGroupsSort(toSortValue(data.field, data.direction));
                            }}
                            initialValue={hasFallbackSortSelection ? parseSortValue(fallbackBoxGroupsSort) : undefined}
                            defaultLabel="Ordenar"
                            dataTestId="fallback-box-groups-sort"
                            size="default"
                            className="h-10 w-full justify-between"
                          />
                        </div>
                      )
                    }
                    testIdPrefix="fallback-group-card"
                    loadingTestId="fallback-group-loading"
                    emptyTestId="fallback-group-empty"
                    hasMore={hasMoreFallbackBoxGroups}
                    isFetchingMore={false}
                    onLoadMore={() => setVisibleCountFallback((prev) => prev + boxGroupsPageSize)}
                    loadMoreTestId="fallback-group-load-more"
                  />
                )}
              </SectionBox>

              {/* Reglas especiales (exceptions) */}
              <SectionBox className="space-y-4" data-testid="packaging-special-rules">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-text">{t('boxes.specialRules.title')}</h3>
                    <p className="text-sm text-subtext mb-2">{t('boxes.specialRules.description')}</p>
                  </div>
                  {boxExceptions.length > 0 && (
                    <Button
                      variant="primary"
                      onClick={() => exceptionModalRef.current?.open()}
                      data-testid="special-rules-new-button"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                      {t('boxes.specialRules.newRule')}
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Spinner hasta que reglas especiales y grupos de cajas estén listos (las tarjetas resuelven grupos). */}
                  {isBoxExceptionsLoading || isBoxGroupsLoading ? (
                    <div className="mt-4 flex justify-center py-8" data-testid="special-rules-loading">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <>
                      {boxExceptions.length === 0 && (
                        <div className="mt-4 rounded-xl border border-dashed border-card-border bg-card-bg px-6 py-8 text-center">
                          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg-info">
                            <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-info" />
                          </div>
                          <p className="text-base font-medium text-text">{t('boxes.specialRules.emptyTitle')}</p>
                          <p className="mx-auto mt-1 max-w-[420px] text-sm text-subtext">
                            {t('boxes.specialRules.empty')}
                          </p>
                          <Button
                            className="mt-4 mx-auto"
                            variant="outline"
                            onClick={() => exceptionModalRef.current?.open()}
                            data-testid="special-rules-empty-cta"
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                            {t('boxes.specialRules.newRule')}
                          </Button>
                        </div>
                      )}

                      {boxExceptions.map((exception, index) => {
                        const palette = getPalette(index);
                        // Primero resolvemos por box_group_id (nuevo contrato de API).
                        // Fallback: búsqueda por packaging_item_id para compatibilidad con payloads viejos.
                        const assignedGroup =
                          allBoxGroups.find((group) => group.id === exception.box_group_id) ??
                          findGroupForItem(allBoxGroups, exception.packaging_item_id);
                        const conditionSummary =
                          exception.conditions && exception.conditions.length > 0
                            ? exception.conditions
                                .map((c) => {
                                  const fieldLabelMap: Record<string, string> = {
                                    total_amount: t('specialRulesModal.fields.total_amount'),
                                    order_total: t('specialRulesModal.fields.total_amount'),
                                    country: t('specialRulesModal.fields.country'),
                                    campaign: t('specialRulesModal.fields.campaign'),
                                    special_campaign: t('specialRulesModal.fields.special_campaign'),
                                    fragile: t('specialRulesModal.fields.fragile'),
                                    product_id: t('specialRulesModal.fields.product_id'),
                                    pack_id: t('specialRulesModal.fields.pack_id'),
                                  };
                                  const operatorLabelMap: Record<string, string> = {
                                    '=': t('specialRulesModal.operators.eq'),
                                    '!=': t('specialRulesModal.operators.neq'),
                                    '>': t('specialRulesModal.operators.gt'),
                                    '<': t('specialRulesModal.operators.lt'),
                                    '>=': t('specialRulesModal.operators.gte'),
                                    '<=': t('specialRulesModal.operators.lte'),
                                    contains: t('specialRulesModal.operators.contains'),
                                  };
                                  const idOperatorLabelMap: Record<string, string> = {
                                    '=': t('specialRulesModal.operators.includes'),
                                    '!=': t('specialRulesModal.operators.notIncludes'),
                                  };
                                  const fieldLabel = fieldLabelMap[c.field] ?? c.field;
                                  const operatorLabel =
                                    c.field === 'product_id' || c.field === 'pack_id'
                                      ? (idOperatorLabelMap[c.operator] ?? c.operator)
                                      : (operatorLabelMap[c.operator] ?? c.operator);
                                  const valueLabel =
                                    typeof c.value_boolean === 'boolean'
                                      ? t(`specialRulesModal.boolean.${c.value_boolean ? 'true' : 'false'}`)
                                      : (c.value_string ??
                                        (typeof c.value_number === 'number' ? String(c.value_number) : ''));
                                  return `${fieldLabel} ${operatorLabel} ${valueLabel}`;
                                })
                                .join(' · ')
                            : t('boxes.specialRules.priorityLabel', { priority: exception.priority });

                        return (
                          <div
                            key={exception.id}
                            className="rounded-lg border border-card-border p-4"
                            data-testid={`special-rule-card-${exception.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-text">{exception.name}</p>
                                  <span
                                    className={cn(
                                      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                      exception.active ? 'bg-bg-success text-success' : 'bg-bg-warning text-warning',
                                    )}
                                  >
                                    {exception.active
                                      ? t('boxes.specialRules.active')
                                      : t('boxes.specialRules.inactive')}
                                  </span>
                                </div>
                                <p className="text-xs text-subtext mt-1">{conditionSummary}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Switch activo/inactivo rápido */}
                                <Switch
                                  checked={exception.active}
                                  onCheckedChange={(checked) =>
                                    handleToggleExceptionActive(exception, Boolean(checked))
                                  }
                                  disabled={isTogglingException === exception.id}
                                  className="mr-1"
                                  data-testid={`special-rule-active-${exception.id}`}
                                  aria-label={t('boxes.specialRules.active')}
                                />
                                {/* Botón editar → abre modal de edición */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 h-auto"
                                  onClick={() => handleOpenEditException(exception)}
                                  aria-label={t('boxes.specialRules.editAria')}
                                  data-testid={`special-rule-edit-${exception.id}`}
                                >
                                  <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5" />
                                </Button>
                                {/* Botón eliminar → confirmación + DELETE a la API */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 h-auto text-error hover:text-error/80"
                                  onClick={() => handleDeleteException(exception)}
                                  disabled={isDeletingException === exception.id}
                                  aria-label={t('boxes.specialRules.deleteAria')}
                                  data-testid={`special-rule-delete-${exception.id}`}
                                >
                                  <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>

                            <div className="mt-3 rounded-lg border border-card-border p-3 flex items-center gap-3">
                              <div
                                className={cn(
                                  'h-9 w-9 rounded-lg flex items-center justify-center',
                                  palette.colorClass,
                                )}
                              >
                                <FontAwesomeIcon icon={faLayerGroup} className={cn('h-3.5 w-3.5', palette.iconClass)} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-text">
                                  {assignedGroup?.name ??
                                    exception.packaging_item?.name ??
                                    `#${exception.box_group_id ?? exception.packaging_item_id ?? 'N/A'}`}
                                </p>
                                <p className="text-xs text-subtext">
                                  <FontAwesomeIcon icon={faBoxOpen} className="mr-1 h-3 w-3" />
                                  {t('boxes.defaultGroup.availableBoxes', {
                                    count: getGroupPackagingItemIds(assignedGroup).length,
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </SectionBox>
            </TabsContent>

            <TabsContent value="marketing" className="mt-4 space-y-4">
              <SectionBox data-testid="packaging-marketing-materials">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 pr-2">
                    <h3 className="text-base font-semibold text-text">{t('marketing.title')}</h3>
                    <p className="text-sm text-subtext">{t('marketing.description')}</p>
                  </div>
                  <Button
                    variant="primary"
                    className="shrink-0"
                    onClick={() => marketingRuleModalRef.current?.open()}
                    data-testid="marketing-new-rule-button"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                    {t('marketing.newRule')}
                  </Button>
                </div>

                {isMarketingCatalogLoading && (
                  <p className="text-sm text-subtext mt-2">{t('marketing.catalogLoading')}</p>
                )}

                {localMarketingRules.length === 0 && (
                  <div className="mt-4 rounded-xl border border-dashed border-card-border bg-card-bg px-6 py-8 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg-info">
                      <FontAwesomeIcon icon={faGift} className="h-5 w-5 text-info" />
                    </div>
                    <p className="text-base font-medium text-text">{t('marketing.emptyTitle')}</p>
                    <p className="mx-auto mt-1 max-w-[480px] text-sm text-subtext">{t('marketing.empty')}</p>
                    <Button
                      className="mt-4 mx-auto"
                      variant="outline"
                      onClick={() => marketingRuleModalRef.current?.open()}
                      data-testid="marketing-empty-cta"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                      {t('marketing.emptyCta')}
                    </Button>
                  </div>
                )}

                {localMarketingRules.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {localMarketingRules.map((rule) => (
                      <div
                        key={rule.clientKey}
                        className="rounded-lg border border-card-border p-4"
                        data-testid={`marketing-rule-card-${rule.ruleId}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-text">{rule.name}</p>
                              {/* Badge de habilitación global (active = entra o no a la evaluación) */}
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                  rule.active ? 'bg-bg-success text-success' : 'bg-bg-warning text-warning',
                                )}
                              >
                                {rule.active ? t('marketing.ruleBadgeActive') : t('marketing.ruleBadgeInactive')}
                              </span>
                            </div>
                            <p className="text-xs text-subtext mt-1">
                              {rule.isAlways ? t('marketing.alwaysAppliesHint') : (rule.conditionSummary ?? '')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Switch activo/inactivo → PUT a la API */}
                            <Switch
                              checked={rule.active}
                              onCheckedChange={(checked) => handleToggleMarketingRuleActive(rule, Boolean(checked))}
                              disabled={isTogglingRule === rule.ruleId}
                              className="mr-1"
                              data-testid={`marketing-rule-active-${rule.ruleId}`}
                              aria-label={t('marketing.ruleBadgeActive')}
                            />
                            {/* Botón editar */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-2 h-auto"
                              onClick={() => handleOpenEditMarketingRule(rule)}
                              data-testid={`marketing-rule-edit-${rule.ruleId}`}
                              aria-label={t('marketing.editRuleAria')}
                            >
                              <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5" />
                            </Button>
                            {/* Botón eliminar → DELETE a la API con confirmación */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-2 text-error hover:text-error/80 h-auto"
                              onClick={() => handleDeleteMarketingRule(rule)}
                              disabled={isDeletingRule === rule.ruleId}
                              data-testid={`marketing-rule-delete-${rule.ruleId}`}
                              aria-label={t('marketing.deleteRuleAria')}
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {rule.actions.map((a, idx) => (
                            <div
                              key={`${a.packaging_item_id}-${idx}`}
                              className="flex min-w-[140px] flex-1 items-center gap-2 rounded-md border border-card-border p-2"
                            >
                              {a.imageUrl ? (
                                <img
                                  src={a.imageUrl}
                                  alt={a.name}
                                  className="h-8 w-8 flex-shrink-0 rounded-md object-cover border border-card-border"
                                />
                              ) : (
                                <div
                                  className={cn(
                                    'h-8 w-8 flex-shrink-0 rounded-md flex items-center justify-center',
                                    a.colorClass,
                                  )}
                                >
                                  <FontAwesomeIcon icon={faGift} className={cn('h-3 w-3', a.iconClass)} />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-text truncate">{a.name}</p>
                                <p className="text-xs text-subtext">
                                  {t('marketing.ruleModal.qty')}: {a.quantity}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Resumen de reglas/materiales de marketing */}
                <div className="mt-4 rounded-xl border border-card-border bg-card-bg p-5">
                  <p className="text-base font-medium text-text">{t('marketing.summaryTitle')}</p>
                  <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <p className="text-sm text-subtext">{t('marketing.summaryActiveRules')}</p>
                      <p className="mt-1 text-3xl font-semibold text-text">{activeMarketingRulesCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-subtext">{t('marketing.summaryDistinctMaterials')}</p>
                      <p className="mt-1 text-3xl font-semibold text-text">{differentMarketingItemsCount}</p>
                    </div>
                  </div>
                </div>
              </SectionBox>
            </TabsContent>
          </Tabs>
        </div>

        {/* Simulador fijo */}
        <aside
          className="mt-6 xl:mt-0 xl:fixed xl:right-6 xl:top-16 xl:w-[340px]"
          data-testid="packaging-simulator-panel"
        >
          <SectionBox title={t('simulator.title')} className="space-y-4 xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto">
            <p className="text-sm text-subtext">{t('simulator.subtitle')}</p>

            {/* ── Importe total ───────────────────────────────── */}
            <div className="my-3 space-y-1.5">
              <label className="text-sm text-text">{t('simulator.totalAmount')}</label>
              <Input
                type="number"
                value={totalAmount}
                onChange={(event) => setTotalAmount(event.target.value)}
                data-testid="simulator-total-amount-input"
              />
            </div>

            {/* ── Campaña (select estático) ────────────────────── */}
            <div className="my-3 space-y-1.5">
              <label className="text-sm text-text">{t('simulator.campaign')}</label>
              <Combobox
                options={campaignOptions}
                value={campaign}
                setValue={setCampaign}
                placeholder={t('simulator.campaignPlaceholder')}
                dataTestId="simulator-campaign-combobox"
              />
            </div>

            {/* ── Destino ─────────────────────────────────────── */}
            <div className="my-3 space-y-1.5">
              <label className="text-sm text-text">{t('simulator.country')}</label>
              <Combobox
                options={countryOptions}
                value={country}
                setValue={setCountry}
                placeholder={t('simulator.countryPlaceholder')}
                dataTestId="simulator-country-combobox"
              />
            </div>

            {/* ── Productos frágiles ───────────────────────────── */}
            <div className="my-3 flex items-center justify-between rounded-md border border-card-border p-3">
              <span className="text-sm text-text">{t('simulator.fragile')}</span>
              <Switch
                checked={fragile}
                onCheckedChange={setFragile}
                data-testid="simulator-fragile-switch"
                aria-label={t('simulator.fragile')}
              />
            </div>

            <Button
              onClick={handleSimulate}
              className="w-full"
              disabled={simulatorMutation.isPending}
              data-testid="simulator-run-button"
            >
              <FontAwesomeIcon icon={faPlay} className="mr-2 h-3.5 w-3.5" />
              {simulatorMutation.isPending ? t('simulator.loading') : t('simulator.run')}
            </Button>

            {/* ── Resultado (oculto hasta simular) ─────────────── */}
            {simulatorData && (
              <div className="my-3 rounded-lg border border-card-border p-3 space-y-3">
                <p className="text-sm font-medium text-text">{t('simulator.resultTitle')}</p>

                {/* Caja seleccionada */}
                <div>
                  <p className="text-xs text-subtext mb-1">{t('simulator.resultBoxLabel')}</p>
                  {simulatorData.box.error && simulatorData.box.error !== 'no_box_ruleset' ? (
                    <p className="text-xs text-danger">{t('simulator.boxEvalError')}</p>
                  ) : evaluatedBoxName ? (
                    <div className="flex items-center gap-2 rounded-md border border-card-border bg-card-bg px-3 py-2">
                      <FontAwesomeIcon icon={faBoxOpen} className="h-4 w-4 text-subtext shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-text">{evaluatedBoxName}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-subtext">{t('simulator.noBoxResult')}</p>
                  )}
                </div>

                {/* Materiales de marketing */}
                <div>
                  <p className="text-xs text-subtext mb-1">{t('simulator.resultMarketingLabel')}</p>
                  {simulatorData.marketing.error && simulatorData.marketing.error !== 'no_marketing_ruleset' ? (
                    <p className="text-xs text-danger">{t('simulator.marketingEvalError')}</p>
                  ) : resolvedMarketingItems.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {resolvedMarketingItems.map((item) => (
                        <div
                          key={`${item.packaging_item_id}-${item.quantity}`}
                          className="flex items-center gap-2 rounded-md border border-card-border bg-card-bg px-3 py-2"
                        >
                          <div
                            className={cn(
                              'h-7 w-7 flex-shrink-0 rounded-md flex items-center justify-center',
                              item.colorClass,
                            )}
                          >
                            <FontAwesomeIcon icon={faTag} className={cn('h-3 w-3', item.iconClass)} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-text truncate">{item.name}</p>
                          </div>
                          <span className="text-xs text-subtext shrink-0">x{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-subtext">{t('simulator.noMarketingItems')}</p>
                  )}
                </div>
              </div>
            )}
          </SectionBox>
        </aside>
      </div>
    </>
  );
};
